import { useNavigate } from "react-router-dom";
import { useCompetitionTimelineEditPageQuery } from "./loaders/CompetitionTimelineEditPage";
import { useIntl } from "react-intl";
import { graphql } from "relay-runtime";
import { useMutation } from "react-relay";
import {
  CompetitionTimelineEditPageMutation$variables,
  CompetitionTimelineEditPageMutation as CompetitionTimelineEditPageMutationType,
} from "./__generated__/CompetitionTimelineEditPageMutation.graphql";
import ErrorPage from "./ErrorPage";
import TimelineEditForm from "@/components/TimelineEditForm";
import { logger } from "@/common/logger";
import { toast } from "@/utils/toast";
import { NaiveDate } from "@/utils/date";
import { relayErrorMessage } from "@/utils/relay";
import { MouseEvent } from "react";

const CompetitionTimelineEditPageMutation = graphql`
  mutation CompetitionTimelineEditPageMutation(
    $id: ID!
    $input: UpdateCompetitionInput!
  ) {
    updateCompetition(id: $id, input: $input) {
      node {
        id
        slug
        timeline {
          timezone
          online
          startDate
          endDate
          steps {
            edges {
              node {
                label
                date
              }
            }
          }
        }
      }
    }
  }
`;

export function CompetitionTimelineEditPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { query } = useCompetitionTimelineEditPageQuery();
  const competition = query.competitionBySlug;

  const [commitMutation, isMutationInFlight] =
    useMutation<CompetitionTimelineEditPageMutationType>(
      CompetitionTimelineEditPageMutation,
    );
  if (!competition) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage({
          defaultMessage: "Competition not found",
        })}
      />
    );
  }
  const timeline = competition.timeline;
  const onDelete = (event: MouseEvent) => {
    event.stopPropagation();
    if (isMutationInFlight) {
      return;
    }

    commitMutation({
      variables: {
        id: competition.id,
        input: {
          timeline: null,
        },
      },
      onCompleted: (results) => {
        toast.success(
          intl.formatMessage({
            defaultMessage: "The timeline has been successfully deleted",
          }),
        );

        navigate(`/competitions/${results.updateCompetition.node.slug}`);
      },
      onError: () => {
        toast.error(
          intl.formatMessage({
            defaultMessage:
              "There was an error while deleting the timeline: please try again later...",
          }),
        );
      },
    });
  };
  return (
    <TimelineEditForm
      defaultValues={{
        timezone: timeline?.timezone,
        online: timeline?.online,
        dateRange:
          timeline?.startDate && timeline.endDate
            ? {
                from: NaiveDate.parse(timeline.startDate),
                to: NaiveDate.parse(timeline.endDate),
              }
            : null,
        steps:
          timeline?.steps &&
          timeline.steps.edges.map(({ node: { label, date } }) => ({
            label,
            date: NaiveDate.parse(date),
          })),
      }}
      onSubmit={({ data, dirtyFields }) => {
        // Since react-hook-form doesn't currently display root-level errors,
        // we handle it manually here. The schema does its job:
        // if a step is out of range, it sets data.steps to undefined.
        // When the user modifies it (making steps a dirty field),
        // it means there's at least one step date outside the valid range.
        // This logic should be removed once
        // https://github.com/react-hook-form/resolvers/issues/561 is resolved and available in production.
        if (!!dirtyFields.steps && !data.steps) {
          toast.error(
            intl.formatMessage(
              {
                defaultMessage:
                  "Each step date must be ordered and strictly within the selected date range: {range} (excluding the start and end dates)",
              },
              {
                range: intl.formatDateTimeRange(
                  (
                    data.dateRange?.from ?? NaiveDate.parse(timeline?.startDate)
                  ).toLocale(),
                  (
                    data.dateRange?.to ?? NaiveDate.parse(timeline?.endDate)
                  ).toLocale(),
                ),
              },
            ),
          );
          return;
        }
        const variables: CompetitionTimelineEditPageMutation$variables = {
          id: competition.id,
          input: {
            timeline: {
              timezone: data.timezone,
              online: data.online,
              startDate: dirtyFields?.dateRange
                ? data.dateRange.from.format()
                : undefined,
              endDate: dirtyFields?.dateRange
                ? data.dateRange.to.format()
                : undefined,
              steps: dirtyFields.steps
                ? data.steps?.length === 0
                  ? null
                  : data.steps?.map(({ date, label }) => ({
                      label,
                      date: date.format(),
                    }))
                : undefined,
            },
          },
        };
        commitMutation({
          variables,
          onError: (error) => {
            logger.error(error);
            toast.error(relayErrorMessage(error));
          },
          onCompleted: (results) => {
            navigate(`/competitions/${results.updateCompetition.node.slug}`);
          },
        });
      }}
      onDelete={onDelete}
      isDisabled={isMutationInFlight}
    />
  );
}
